import { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import OverviewIcon from '@mui/icons-material/Visibility';
import MapIcon from '@mui/icons-material/Map';
import JudgeIcon from '@mui/icons-material/SportsMartialArts';
import RefereeIcon from '@mui/icons-material/Person';
import TeamsIcon from '@mui/icons-material/Groups';
import TournamentIcon from '@mui/icons-material/EmojiEvents';
import BlocksIcon from '@mui/icons-material/GridView';
import CourtsIcon from '@mui/icons-material/Stadium';
import FieldsIcon from '@mui/icons-material/Map';
import TimetableIcon from '@mui/icons-material/Schedule';
import DarkModeIcon from '@mui/icons-material/Brightness4';
import LightModeIcon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountIcon from '@mui/icons-material/AccountCircle';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import UsersIcon from '@mui/icons-material/ManageAccounts';
import PrintIcon from '@mui/icons-material/Print';
import AssignmentIcon from '@mui/icons-material/Assignment';
import QuizIcon from '@mui/icons-material/Quiz';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';

const DRAWER_WIDTH = 260;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles?: string[];
  children?: NavItem[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
  collapsible?: boolean;
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Head Judge',
    items: [
      { label: 'Head Judge Log', path: '/admin/head-judge-log', icon: <AssignmentIcon /> },
      { 
        label: 'Teams', 
        path: '/admin/teams', 
        icon: <TeamsIcon />,
        children: [
          { label: 'Yleinen', path: '/admin/teams?league=yle', icon: <TeamsIcon /> },
          { label: 'PRO', path: '/admin/teams?league=pro', icon: <TeamsIcon /> },
          { label: 'Firma', path: '/admin/teams?league=frm', icon: <TeamsIcon /> },
          { label: 'Diagnostics', path: '/admin/teams/diagnostics', icon: <BugReportIcon /> },
        ],
      },
      { label: 'Games', path: '/admin/games', icon: <TournamentIcon /> },
      { label: 'Blocks', path: '/admin/blocks', icon: <BlocksIcon /> },
      { label: 'Courts', path: '/admin/courts', icon: <CourtsIcon /> },
      { label: 'Referees', path: '/admin/referees', icon: <RefereeIcon /> },
      { label: 'Judge Interface', path: '/judge', icon: <JudgeIcon /> },
    ],
  },
  {
    label: 'Admin',
    collapsible: true,
    items: [
      { label: 'Dashboard', path: '/admin', icon: <DashboardIcon /> },
      { label: 'Overview (TiKe)', path: '/admin/overview', icon: <OverviewIcon /> },
      { label: 'Field Map', path: '/admin/field-map', icon: <MapIcon /> },
      { label: 'Fields', path: '/admin/fields', icon: <FieldsIcon /> },
      { label: 'Field Groups', path: '/admin/field-groups', icon: <FieldsIcon /> },
      { label: 'Users', path: '/admin/users', icon: <UsersIcon /> },
      { label: 'Printing', path: '/admin/printing', icon: <PrintIcon /> },
      { label: 'Timetable', path: '/admin/timetable', icon: <TimetableIcon /> },
      { label: 'VIP Court Prefs', path: '/admin/court-prefs', icon: <TeamsIcon /> },
      { label: 'Settings', path: '/admin/settings', icon: <SettingsIcon /> },
      { label: 'FAQ', path: '/admin/faq', icon: <QuizIcon /> },
    ],
  },
];

// Flatten for route matching (including children)
const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => 
  group.items.flatMap((item) => item.children ? [item, ...item.children] : [item])
);

/**
 * Find the best matching nav item for the current path and search params.
 * Returns the nav item with the longest matching path prefix.
 */
function findBestMatch(pathname: string, search: string): NavItem | null {
  return NAV_ITEMS.reduce<NavItem | null>((best, item) => {
    // Check for exact match with query params
    const itemPathname = item.path.split('?')[0];
    const itemSearch = item.path.includes('?') ? '?' + item.path.split('?')[1] : '';
    
    // Exact match including query params
    if (pathname === itemPathname && search === itemSearch) {
      if (item.path.length > (best?.path.length ?? 0)) {
        return item;
      }
    }
    // Path prefix match (for items without query params)
    const matches = !itemSearch && (
      pathname === itemPathname || pathname.startsWith(itemPathname + '/')
    );
    if (matches && item.path.length > (best?.path.length ?? 0)) {
      return item;
    }
    return best;
  }, null);
}

export function AppShell() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { themeMode, toggleTheme, sidebarOpen, toggleSidebar } = useUIStore();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({
    '/admin/teams': true, // Teams expanded by default
  });
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const toggleExpanded = (path: string) => {
    setExpandedItems((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const toggleGroupCollapsed = (label: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const bestMatch = findBestMatch(location.pathname, location.search);

  const drawer = (
    <Box>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          TuHO v4
        </Typography>
      </Toolbar>
      <Divider />
      {NAV_GROUPS.map((group, groupIndex) => {
        const isGroupCollapsed = collapsedGroups[group.label] ?? false;
        
        return (
          <Box key={group.label}>
            {groupIndex > 0 && <Divider sx={{ my: 1 }} />}
            <Box
              onClick={group.collapsible ? () => toggleGroupCollapsed(group.label) : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                pt: 2,
                pb: 1,
                cursor: group.collapsible ? 'pointer' : 'default',
                '&:hover': group.collapsible ? {
                  backgroundColor: theme.palette.action.hover,
                } : undefined,
                borderRadius: 1,
                mx: 1,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                }}
              >
                {group.label}
              </Typography>
              {group.collapsible && (
                isGroupCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />
              )}
            </Box>
            <Collapse in={!isGroupCollapsed} timeout="auto">
              <List disablePadding>
                {group.items.map((item) => {
                  const isSelected = bestMatch?.path === item.path;
                  const hasChildren = item.children && item.children.length > 0;
                  const isExpanded = expandedItems[item.path] ?? false;
                  const isChildSelected = hasChildren && item.children?.some(
                    (child) => bestMatch?.path === child.path
                  );

                  return (
                    <Box key={item.path}>
                      <ListItem disablePadding>
                        <ListItemButton
                          component={hasChildren ? 'div' : RouterLink}
                          to={hasChildren ? undefined : item.path}
                          onClick={hasChildren ? () => toggleExpanded(item.path) : undefined}
                          selected={isSelected || isChildSelected}
                          sx={{
                            mx: 1,
                            borderRadius: 1,
                            '&.Mui-selected': {
                              backgroundColor: theme.palette.primary.main + '20',
                              '&:hover': {
                                backgroundColor: theme.palette.primary.main + '30',
                              },
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              color: (isSelected || isChildSelected) ? theme.palette.primary.main : 'inherit',
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText primary={item.label} />
                          {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
                        </ListItemButton>
                      </ListItem>
                      {hasChildren && (
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <List disablePadding>
                            {item.children?.map((child) => {
                              const isChildItemSelected = bestMatch?.path === child.path;
                              return (
                                <ListItem key={child.path} disablePadding>
                                  <ListItemButton
                                    component={RouterLink}
                                    to={child.path}
                                    selected={isChildItemSelected}
                                    sx={{
                                      mx: 1,
                                      pl: 4,
                                      borderRadius: 1,
                                      '&.Mui-selected': {
                                        backgroundColor: theme.palette.primary.main + '20',
                                        '&:hover': {
                                          backgroundColor: theme.palette.primary.main + '30',
                                        },
                                      },
                                    }}
                                  >
                                    <ListItemText primary={child.label} />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        </Collapse>
                      )}
                    </Box>
                  );
                })}
              </List>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${sidebarOpen ? DRAWER_WIDTH : 0}px)` },
          ml: { sm: `${sidebarOpen ? DRAWER_WIDTH : 0}px` },
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={toggleSidebar}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {bestMatch?.label || 'Dashboard'}
          </Typography>

          {/* Theme toggle */}
          <Tooltip title={`Switch to ${themeMode === 'light' ? 'dark' : 'light'} mode`}>
            <IconButton color="inherit" onClick={toggleTheme}>
              {themeMode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>

          {/* User menu */}
          <Tooltip title="Account">
            <IconButton onClick={handleMenuOpen} sx={{ ml: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main }}>
                {user?.first_name?.[0] || user?.username?.[0] || <AccountIcon />}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {user?.email || 'Not logged in'}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/profile');
              }}
            >
              <ListItemIcon>
                <AccountIcon fontSize="small" />
              </ListItemIcon>
              My Information
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: sidebarOpen ? DRAWER_WIDTH : 0 }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={sidebarOpen}
          onClose={toggleSidebar}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="persistent"
          open={sidebarOpen}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${sidebarOpen ? DRAWER_WIDTH : 0}px)` },
          height: '100vh',
          overflow: 'auto',
          backgroundColor: theme.palette.background.default,
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}
        <Outlet />
      </Box>
    </Box>
  );
}

